import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc, IsSigningObject } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { useForm } from '@tanstack/react-form';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import { EULA_TEXT, EULAheader, EULAInstructions, EULASectionHeader, EULASectionText } from '../../../../components/EULA';
export const Route = createFileRoute('/_authenticated/$patientId/info/consentForms')({
  component: FormsPage,
});
import { Button } from '../../../../components/buttons/Button';
import { NavArrowRight, NavArrowDown } from 'iconoir-react';
import { FormsSignatureArea } from '../../../../components';
import { ConsentForCare, ConsentSectionText, ConsentSubSectionText } from '../../../../components/CONSENT';
import { HIPAASectionText } from '../../../../components/HIPAA/hipaaSectionText';
import { HIPAA } from '../../../../components/HIPAA/HIPAA';
import { FormInput } from '../../../../components/forms/FormInput';
import { setDisabledPages } from '../../../../data/navigation/utilities/setDisabledPages';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

function FormsPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const { isFetched: patientInfoIsFetched } = patientDocQuery;
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [attestedEULA, setAttestedEULA] = React.useState(false);
  const [attestedHipaa, setAttestedHipaa] = React.useState(false);
  const [attestedConsentForCare, setAttestedConsentForCare] = React.useState(false);
  const { mutate } = useUpdateChartSection();
  const [eulaOpen, setEulaOpen] = React.useState(false);
  const [hipaaOpen, setHipaaOpen] = React.useState(false);
  const [consentForCareOpen, setConsentForCareOpen] = React.useState(false);
  // const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isSigning, setIsSigning] = React.useState({
    patientFinal: false,
    witness1: false,
    witness2: false,
    hipaa: false,
    consent: false,
    forms: false,
    abnMedicare: false,
    abn: false,
    aob1: false,
    aob2: false,
  } as IsSigningObject);
  const [canText, setCanText] = React.useState<string>('no');
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  const handleCanTextStatusChange = (status: string) => {
    setCanText(status);
    mutatePatientDoc(
      {
        patientId: patientId,
        data: {
          canText: canText === 'yes' ? false : true,
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  React.useEffect(() => {
    if (patientId && patientInfoIsFetched && patientInfo?.canText === true) {
      setCanText('yes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, patientInfo]);

  const form = useForm({
    defaultValues: {
      exceptions: chart?.HIPAA_AUTH?.exceptions ? chart.HIPAA_AUTH.exceptions : '',
      signature1DataURI: chart?.HIPAA_AUTH?.signature1DataURI ? chart.HIPAA_AUTH.signature1DataURI : '',
    },
    onSubmit: async ({ value }) => {
      // Write HIPAA_AUTH
      mutate(
        {
          patientId: patientId,
          docData: {
            HIPAA_AUTH: value,
          },
        },
        {
          onSuccess: () => {
            // Write EULA on submit (not on checkbox click) to avoid toggle bugs
            mutate(
              {
                patientId: patientId,
                docData: {
                  EULA: { agreed: true },
                },
              },
              {
                onError: () => {
                  notifyChartSaveError();
                  setIsSubmitted(false);
                },
              },
            );
          },
          onError: () => {
            notifyChartSaveError();
            setIsSubmitted(false);
          },
        },
      );
    },
  });

  const handleEulaAttestationCheckboxClick = () => {
    setAttestedEULA(true);
  };

  const handleHipaaAttestationCheckboxClick = () => {
    setAttestedHipaa(!attestedHipaa);
  };
  const handleConsentForCareAttestationCheckboxClick = () => {
    setAttestedConsentForCare(!attestedConsentForCare);
  };

  React.useEffect(() => {
    if (chart && chart.EULA?.agreed) {
      setAttestedEULA(true);
    }

    if (chart && chart.HIPAA_AUTH && chart.HIPAA_AUTH.signature1DataURI?.length > 0) {
      setAttestedHipaa(true);
    }

    if (chart && chart.HIPAA_AUTH && chart.HIPAA_AUTH.signature1DataURI?.length > 0) {
      setAttestedConsentForCare(true);
    }
    if (chart && patientInfo) {
      setDisabledPages(patientInfo, chart);
    }
  }, [chart, patientInfo]);

  // Read-only mode (D-020): only if all consents were already complete when the chart first loaded.
  // This prevents locking the form mid-onboarding when the patient just finished signing.
  // CU-86agmta3g: use hipaaConsentState as primary check; fall back to signatures for pre-migration.
  const initialConsentState = React.useRef<boolean | null>(null);
  if (initialConsentState.current === null && chartQuery.isFetched && chart) {
    const consentGranted = chart.HIPAA_AUTH?.hipaaConsentState?.status === 'granted'
      || (!chart.HIPAA_AUTH?.hipaaConsentState && Boolean(
        chart.HIPAA_AUTH?.signature1DataURI?.length &&
        chart.CONSENT_FOR_CARE?.signature1DataURI?.length
      ));
    initialConsentState.current = Boolean(chart.EULA?.agreed && consentGranted);
  }
  const allConsentsComplete = initialConsentState.current === true;

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="forms-page" className="flex flex-col items-center">
          <Heading id="forms-page-header" textContent="Consent Forms" />
          <div id="forms-page-main" className="flex w-5/6">
            <div id="forms-page-content" className="flex flex-col items-start">
              <div id="eula-area">
                <div className="flex flex-row items-center">
                  {eulaOpen ? (
                    <NavArrowDown onClick={() => setEulaOpen(!eulaOpen)} />
                  ) : (
                    <NavArrowRight onClick={() => setEulaOpen(!eulaOpen)} />
                  )}
                  <p className="ml-2 text-2xl font-bold">END USER LICENSE AGREEMENT</p>
                </div>
                {eulaOpen && (
                  <div className="h-[65vh] overflow-scroll">
                    <EULAheader text={EULA_TEXT[0]} />
                    <div className="">
                      <EULAInstructions text={EULA_TEXT[1]} />
                      <EULASectionHeader text={EULA_TEXT[2]} />
                      <EULASectionText text={EULA_TEXT[3]} />
                      <EULASectionText text={EULA_TEXT[4]} />
                      <EULASectionHeader text={EULA_TEXT[5]} />
                      <EULASectionHeader text={EULA_TEXT[6]} />
                      <EULASectionHeader text={EULA_TEXT[7]} />
                      <EULASectionText text={EULA_TEXT[8]} />
                      <EULASectionText text={EULA_TEXT[9]} />
                      <EULASectionText text={EULA_TEXT[10]} />
                      <EULASectionText text={EULA_TEXT[11]} />
                      <EULASectionText text={EULA_TEXT[12]} />
                      <EULASectionText text={EULA_TEXT[13]} />
                      <EULASectionHeader text={EULA_TEXT[14]} />
                      <EULASectionText text={EULA_TEXT[15]} />
                      <EULASectionText text={EULA_TEXT[16]} />
                      <EULASectionText text={EULA_TEXT[17]} />
                      <EULASectionText text={EULA_TEXT[18]} />
                      <EULASectionText text={EULA_TEXT[19]} />
                      <EULASectionHeader text={EULA_TEXT[20]} />
                      <EULASectionHeader text={EULA_TEXT[21]} />
                      <EULASectionHeader text={EULA_TEXT[22]} />
                      <EULASectionHeader text={EULA_TEXT[23]} />
                      <EULASectionHeader text={EULA_TEXT[24]} />
                    </div>
                  </div>
                )}
                <div id="eula-attestation-checkbox-container" className="my-4 ml-4 flex w-full flex-row justify-center">
                  <Checkbox
                    id="eula-attestation-checkbox"
                    name="eula-attestation"
                    onChange={() => {
                      if (!allConsentsComplete) handleEulaAttestationCheckboxClick();
                    }}
                    checked={attestedEULA}
                    disabled={allConsentsComplete}
                    label="I attest that I have read the End User License Agreement and agree to be bound by the conditions set
                    therein."
                  />
                </div>
              </div>
              <div id="hipaa-area">
                <div className="flex flex-row items-center">
                  {hipaaOpen ? (
                    <NavArrowDown onClick={() => setHipaaOpen(!hipaaOpen)} />
                  ) : (
                    <NavArrowRight onClick={() => setHipaaOpen(!hipaaOpen)} />
                  )}
                  <p className="ml-2 text-2xl font-bold">HIPAA AGREEMENT</p>
                </div>
                {hipaaOpen && (
                  <>
                    <div className="mt-4 h-[30vh] overflow-scroll">
                      <HIPAASectionText text={HIPAA.Statement.One} />
                      <HIPAASectionText text={HIPAA.Statement.Two} />
                      <HIPAASectionText text={HIPAA.Statement.Three} />
                      <div>
                        <form.Field
                          name="exceptions"
                          children={(field) => (
                            <FormInput
                              label="Exceptions"
                              id="hipaa-exceptions"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => { if (!allConsentsComplete) field.handleChange(e.target.value); }}
                              placeholder="none"
                            />
                          )}
                        />
                      </div>
                      <HIPAASectionText text={HIPAA.Statement.Four} />
                      <HIPAASectionText text={HIPAA.Statement.Five} />
                      <HIPAASectionText text={HIPAA.Statement.Six} />
                      <HIPAASectionText text={HIPAA.Statement.Seven} />
                      <HIPAASectionText text={HIPAA.Statement.Eight} />
                    </div>
                  </>
                )}
                <div
                  id="hipaa-attestation-checkbox-container"
                  className="my-4 ml-4 flex w-full flex-row justify-center"
                >
                  <Checkbox
                    id="hipaa-attestation-checkbox"
                    name="hipaa-attestation"
                    onChange={() => {
                      if (!allConsentsComplete) handleHipaaAttestationCheckboxClick();
                    }}
                    checked={attestedHipaa}
                    disabled={allConsentsComplete}
                    label="I attest that I have read the HIPAA privacy policy and agree to be bound by the conditions set
                    therein including the exceptions listed above."
                  />
                </div>
              </div>
              <div id="consent-for-care-area">
                <div className="flex flex-row items-center">
                  {consentForCareOpen ? (
                    <NavArrowDown onClick={() => setConsentForCareOpen(!consentForCareOpen)} />
                  ) : (
                    <NavArrowRight onClick={() => setConsentForCareOpen(!consentForCareOpen)} />
                  )}
                  <p className="ml-2 text-2xl font-bold">CONSENT FOR CARE AGREEMENT</p>
                </div>
                {consentForCareOpen && (
                  <div id="consent-for-care-page-page-content" className="flex h-[35vh] flex-col overflow-scroll">
                    <ConsentSectionText text={ConsentForCare.StatementA} />
                    <ConsentSectionText text={ConsentForCare.StatementB} />
                    <ConsentSectionText text={ConsentForCare.StatementC} />
                    <ConsentSectionText text={ConsentForCare.StatementD} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD1} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD2} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD3} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD4} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD5} />
                    <ConsentSectionText text={ConsentForCare.StatementE} />
                  </div>
                )}
                <div
                  id="consent-for-care-attestation-checkbox-container"
                  className="my-4 ml-4 flex w-full flex-row justify-center"
                >
                  <Checkbox
                    id="consent-for-care-attestation-checkbox"
                    name="consent-for-care-attestation"
                    onChange={() => {
                      if (!allConsentsComplete) handleConsentForCareAttestationCheckboxClick();
                    }}
                    checked={attestedConsentForCare}
                    disabled={allConsentsComplete}
                    label="I attest that I have read the Consent For Care policy and agree to be bound by the conditions set
                    therein."
                  />
                </div>
              </div>
              <div id="can-text-area" className="my-4">
                <fieldset>
                  <legend className="text-xl font-bold">
                    Do you agree to opt-ln to receive informational and/or scheduling text messages? You may opt-out at
                    any time replying STOP.
                  </legend>
                  <div>
                    <div className="ml-4">
                      <input
                        type="radio"
                        id="Yes"
                        name="canTextStatus"
                        value="yes"
                        checked={canText === 'yes'}
                        disabled={allConsentsComplete}
                        onChange={(e) => {
                          setIsSubmitted(false);
                          handleCanTextStatusChange(e.target.value);
                        }}
                      />
                      <label className="ml-2 text-xl" htmlFor="Yes">
                        Yes
                      </label>
                    </div>

                    <div className="ml-4">
                      <input
                        type="radio"
                        id="No"
                        name="canTextStatus"
                        value="no"
                        checked={canText === 'no'}
                        disabled={allConsentsComplete}
                        onChange={(e) => handleCanTextStatusChange(e.target.value)}
                      />
                      <label className="ml-2 text-xl" htmlFor="No">
                        No
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>
              {attestedEULA && attestedHipaa && attestedConsentForCare && !allConsentsComplete && (
                <FormsSignatureArea
                  elementId={'patient-forms-sig-'}
                  data={chart}
                  patientId={patientId}
                  isSigning={isSigning}
                  setIsSigning={setIsSigning}
                  sigName="forms"
                  hipaaExceptions={form.getFieldValue('exceptions')}
                />
              )}
              {allConsentsComplete && chart?.HIPAA_AUTH?.signature1DataURI && (
                <div className="mt-4">
                  <p className="text-lg font-bold">Signature on file:</p>
                  <img
                    src={chart.HIPAA_AUTH.signature1DataURI}
                    alt="Patient Consent Signature"
                    className="w-1/2"
                  />
                </div>
              )}
            </div>
          </div>
          {allConsentsComplete && (
            <p className="mt-6 text-center text-sm text-gray-500">
              This page is locked for editing. Your consent is on file.
            </p>
          )}
          {!allConsentsComplete && (
            <div id="nav" className="flex flex-col">
              <Button
                id="submit-info"
                type="button"
                onClick={() => {
                  form.handleSubmit();
                  setIsSubmitted(true);
                }}
                textContent="Submit"
                disabled={!attestedEULA || !attestedConsentForCare || !attestedHipaa}
                submitted={isSubmitted}
              />
              {isSubmitted &&
                attestedEULA &&
                attestedHipaa &&
                attestedConsentForCare &&
                (chart?.HIPAA_AUTH?.hipaaConsentState?.status === 'granted' ||
                  (chart?.HIPAA_AUTH?.signature1DataURI?.length ?? 0) > 0) && (
                  <div className="flex flex-col items-center">
                    <button
                      id="submit-form-info"
                      title="Submit Form Info"
                      onClick={handleNextButtonClick}
                      disabled={!attestedEULA || !attestedConsentForCare || !attestedHipaa || !isSubmitted}
                      className={classNames.nextButton}
                    >
                      Next
                    </button>
                  </div>
                )}
            </div>
          )}
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
